<script setup lang="ts">
import { computed } from 'vue';
import Carousel from 'primevue/carousel';
import glassCoinsInCircleImage from '@/assets/images/glass-coins-in-circle.png';
import purpleCoin3DImage from '@/assets/images/3d-purple-coin.png';
import { useRollingFundingInquiries } from '@/composables/useRollingFundingInquiries';
import { useStore } from 'vuex';

const store = useStore();

const emit = defineEmits(['spotlightClicked']);

const { latestValidRollingFundingInquiry } = useRollingFundingInquiries();

const handleSpotlightClick = (slide: string): void => {
  emit('spotlightClicked', { slide });
};

const businessCanSubmitRollingFundingInquiry = computed<boolean>(
  () => store.getters.businessCanSubmitRollingFundingInquiry,
);

interface FeaturedSlide {
  title: string;
  description: string;
  icon: string;
  cta?: {
    label: string;
    action: (event: Event) => void;
  };
}

const featuredSlides = computed<FeaturedSlide[]>(() => {
  let slides: FeaturedSlide[] = [];

  const cashAdvanceSlide: FeaturedSlide = {
    title: 'Introducing Cash Advance',
    description:
      'Access working capital instantly, giving you the flexibility to invest in any pressing business needs.',
    icon: purpleCoin3DImage,
  };

  const rollingFundingSlide: FeaturedSlide = {
    title: 'Always-On Capital',
    description:
      'Unlock capital that automatically replenishes as you make payments.',
    icon: glassCoinsInCircleImage,
    cta: {
      label: latestValidRollingFundingInquiry.value
        ? 'Our team will reach out shortly.'
        : 'Learn more',
      action: () => handleSpotlightClick('Rolling Funding'),
    },
  };

  if (businessCanSubmitRollingFundingInquiry.value) {
    slides.push(rollingFundingSlide);
  }

  slides.push(cashAdvanceSlide);

  return slides;
});
</script>

<template>
  <div :class="$style.carouselWrapper">
    <Carousel
      :value="featuredSlides"
      :showNavigators="false"
      :showIndicators="featuredSlides.length > 1"
      :class="$style.carousel"
      :indicatorsContentClass="$style.customIndicators"
      :autoplayInterval="featuredSlides.length > 1 ? 10000 : undefined"
    >
      <template #item="{ data }">
        <div :class="$style.slideWrapper">
          <div :class="$style.imageContainer">
            <img
              :src="data.icon"
              :alt="data.title"
              :class="$style.slideImage"
            />
          </div>
          <div :class="$style.textContainer">
            <h3 :class="$style.slideTitle">{{ data.title }}</h3>
            <p :class="$style.slideDescription">{{ data.description }}</p>
            <p
              v-if="data.cta"
              :class="$style.slideLink"
              @click="data.cta.action()"
            >
              {{ data.cta.label }}
            </p>
          </div>
        </div>
      </template>
    </Carousel>
  </div>
</template>

<style lang="less" module>
.carouselWrapper {
  border: none;
  border-radius: 4px;
  overflow: hidden;
  width: 262px;
  position: relative;
  margin-bottom: 40px;

  @media (max-width: 1320px) {
    width: 100%;
  }
}

html body .carouselWrapper [class^='p-carousel-container'] {
  border: 1px solid #ceced0;
  border-radius: 4px;

  @media screen and (max-width: 1320px) {
    height: 235px;
  }

  @media screen and (max-width: 500px) {
    height: auto;
  }
}

html body .carouselWrapper [class^='p-carousel-items-content'] {
  background:
    linear-gradient(0deg, #fbf7ff, #e5d3f7),
    radial-gradient(
      91.07% 74.67% at 75.36% 28.8%,
      rgba(127, 48, 255, 0.2) 0%,
      rgba(255, 255, 255, 0) 100%
    );
  border-radius: 3px;
}

.slideWrapper {
  display: flex;
  flex-direction: column;
  align-items: center;

  .imageContainer {
    padding: 46px;
    flex-shrink: 0;
  }

  .textContainer {
    padding: 8px 16px 16px;
  }

  .slideTitle {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 8px;
    margin-top: 0;
    color: @black;
    line-height: 28px;
  }

  .slideDescription {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    margin-bottom: 0;
    color: @color-grey-400;
  }

  @media screen and (max-width: 1320px) {
    flex-direction: row;

    .imageContainer {
      width: 235px;
    }
  }

  @media screen and (max-width: 500px) {
    flex-direction: column;
    height: unset;
  }
}

.slideLink {
  color: @color-cc-purple-200;
  font-family: @regular-font;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 0;
  margin-top: 8px;
}

.customIndicators {
  button {
    border-radius: 50% !important;
    width: 12px !important;
    height: 12px !important;
  }
}
</style>
